import { getDataClient } from "@/lib/dataClient";
import { useDataQuery } from "@/hooks/useDataQuery";

// Yoklama (attendance) veri katmanı.

export type AttendanceStatus = "present" | "absent" | "late";

export interface AttendanceRecord {
  studentUserId: string;
  status: AttendanceStatus;
}

export interface StudentAttendanceRow {
  id: string;
  liveClassId: string;
  classTitle: string;
  subject: string;
  date: string;
  status: AttendanceStatus;
}

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: "Katıldı",
  absent: "Katılmadı",
  late: "Geç Kaldı",
};

export const ATTENDANCE_STATUS_COLORS: Record<AttendanceStatus, string> = {
  present: "bg-accent-100 text-accent-800",
  absent: "bg-red-50 text-red-700",
  late: "bg-secondary-100 text-secondary-800",
};

// Öğretmen: belirli bir canlı dersin yoklama kayıtlarını çeker.
export function useClassAttendance(liveClassId: string | null) {
  return useDataQuery<Record<string, AttendanceStatus>>(
    async () => {
      if (!liveClassId) return {};
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("attendance")
        .select("student_user_id, status")
        .eq("live_class_id", liveClassId);
      if (error) throw error;
      const map: Record<string, AttendanceStatus> = {};
      (data ?? []).forEach((r: { student_user_id: string; status: AttendanceStatus }) => {
        map[r.student_user_id] = r.status;
      });
      return map;
    },
    [liveClassId],
  );
}

// Öğretmen: öğrencilerin yoklamasını işaretler.
export async function markAttendance(liveClassId: string, records: AttendanceRecord[]): Promise<void> {
  const dataClient = getDataClient();
  const payload = records.map((r) => ({ student_user_id: r.studentUserId, status: r.status }));
  const { error } = await dataClient.rpc("mark_attendance", {
    p_live_class_id: liveClassId,
    p_records: payload,
  });
  if (error) throw error;
}

function fullDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// Öğrenci/veli: bir öğrencinin yoklama geçmişini çeker (canlı ders bilgisiyle birlikte).
export function useStudentAttendance(studentUserId: string | null) {
  return useDataQuery<StudentAttendanceRow[]>(
    async () => {
      if (!studentUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("attendance")
        .select("id, live_class_id, status, created_at")
        .eq("student_user_id", studentUserId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const classIds = Array.from(new Set((data ?? []).map((r: { live_class_id: string }) => r.live_class_id)));
      const classMap = new Map<string, { title: string; subject: string; starts_at: string }>();
      if (classIds.length > 0) {
        const { data: cls, error: cErr } = await dataClient
          .from("live_classes")
          .select("id, title, subject, starts_at")
          .in("id", classIds);
        if (!cErr) {
          (cls ?? []).forEach((c: { id: string; title: string; subject: string; starts_at: string }) => {
            classMap.set(c.id, c);
          });
        }
      }

      return (data ?? []).map(
        (r: { id: string; live_class_id: string; status: AttendanceStatus; created_at: string }) => {
          const c = classMap.get(r.live_class_id);
          return {
            id: r.id,
            liveClassId: r.live_class_id,
            classTitle: c?.title ?? "Canlı Ders",
            subject: c?.subject ?? "",
            date: fullDate(c?.starts_at ?? r.created_at),
            status: r.status,
          };
        },
      );
    },
    [studentUserId],
  );
}